import { X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Dialog } from '@archipro-design/aria';
import { CommentViewer } from '@modules/build-hub-comment/components/CommentViewer';
import { CommentBox } from '@modules/build-hub-comment/components/CommentBox';
import { useFetcher } from '@remix-run/react';
import type { loader } from '~/routes/remix-api.app.build-hub-item-comments.$id';
import { LIST_BUILD_HUB_ITEM_COMMENTS_API_ROUTE } from '~/routes/remix-api.app.build-hub-item-comments.$id';
import type {
    CommentSubmitPayload,
    Comment,
} from '@modules/build-hub-comment/types';
import { useUser } from '@modules/user';
import { AnimatePresence, motion } from 'framer-motion';

const POLL_INTERVAL = 5000;

export const useCommentDialog = () => {
    const [itemId, setItemID] = useState<number | null>(null);
    const [isPosting, setIsPosting] = useState(false);
    const fetcher = useFetcher<typeof loader>();
    const user = useUser();
    const scrollRef = useRef<HTMLDivElement>(null);

    const openDialog = (id: number) => {
        setItemID(id);
        setIsPosting(false);
    };
    const closeDialog = () => setItemID(null);

    // fetch + poll
    useEffect(() => {
        if (!itemId) return;

        const url = `${LIST_BUILD_HUB_ITEM_COMMENTS_API_ROUTE}/${itemId}`;
        fetcher.load(url);

        const interval = setInterval(() => {
            fetcher.load(url);
            setIsPosting(false);
        }, POLL_INTERVAL);

        return () => clearInterval(interval);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId]);

    const handlePosted = (_payload: CommentSubmitPayload) => {
        setIsPosting(true);
    };

    const comments: Comment[] = fetcher.data?.comments ?? [];

    // scroll to bottom when comments or spinner changes
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [comments.length, isPosting]);

    const commentDialog = (className?: string) => (
        <Dialog
            open={!!itemId}
            onCancel={closeDialog}
            className={`w-full max-w-[500px] ${className ?? ''}`}
            header={null}
            footer={null}
            content={
                itemId && (
                    <div className="flex flex-col gap-4">
                        <div className="flex w-full items-center justify-between">
                            <h3 className="text-lg font-semibold">Comments</h3>
                            <button
                                onClick={closeDialog}
                                className="rounded p-1 hover:bg-gray-100"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div
                            ref={scrollRef}
                            className="max-h-[400px] space-y-2 overflow-y-auto pr-1"
                        >
                            {fetcher.state === 'loading' && !fetcher.data ? (
                                <p className="text-sm text-gray-500">
                                    Loading comments...
                                </p>
                            ) : (
                                <>
                                    <AnimatePresence initial={false}>
                                        {comments.map((c) => {
                                            const isMine =
                                                c.author.id === user.ID;
                                            return (
                                                <motion.div
                                                    key={c.id}
                                                    initial={{
                                                        opacity: 0,
                                                        x: isMine ? 40 : -40,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        x: 0,
                                                    }}
                                                    exit={{ opacity: 0 }}
                                                    transition={{
                                                        duration: 0.2,
                                                    }}
                                                >
                                                    <CommentViewer
                                                        comments={[c]}
                                                    />
                                                </motion.div>
                                            );
                                        })}
                                    </AnimatePresence>

                                    <AnimatePresence>
                                        {isPosting && (
                                            <motion.div
                                                key="spinner"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="mt-2 flex justify-end pr-2"
                                            >
                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                    <svg
                                                        className="h-4 w-4 animate-spin text-gray-400"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        ></circle>
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                        ></path>
                                                    </svg>
                                                    Sending…
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </>
                            )}
                        </div>

                        <CommentBox
                            documentId={String(itemId)}
                            onPosted={handlePosted}
                        />
                    </div>
                )
            }
        />
    );

    return { openDialog, closeDialog, commentDialog };
};
